import axios from "axios";

import { request } from "@shared/utils";
import { API, METHODS } from "@shared/constants";
import { BaseFilter } from "@shared/interfaces";
import { CaseModel } from "@shared/models";

import {
  CaseDocumentDto,
  CaseFileDto,
  CheckedCaseFile,
  ChooseMedicalFieldValueDto,
  VerifyMedicalFieldDto,
  CreateCaseDto,
  ParseCaseDocumentDto,
  RemoveCaseDocumentDto,
  UploadCaseFileDto,
} from "../interface";
import { CasesReportStatusDtoInterface } from "../interface/CasesReportStatusDto.interface";

export default {
  getCasesList: (payload: BaseFilter) => request(METHODS.GET, API.CASE.GET_CASES_LIST)({ params: payload }),
  getCasesStatuses: (ids: number[]) =>
    request(METHODS.GET, API.CASE.GET_CASES_STATUSES)({ params: { ids: ids.join(",") } }),
  getCase: (id: number) => request(METHODS.GET, API.CASE.GET_CASE(id))(),
  deleteCase: (id: number) => request(METHODS.DELETE, API.CASE.DELETE_CASE(id))(),
  restartProcessingCase: (id: number) => request(METHODS.POST, API.CASE.RESTART_PROCESSING_CASE(id))(),
  createCase: (payload: CreateCaseDto) => request(METHODS.POST, API.CASE.CREATE_CASE)(payload),
  updateCaseReportStatus: (id: number, payload: Pick<CasesReportStatusDtoInterface, "report_status">) =>
    request(METHODS.PATCH, API.CASE.UPDATE_CASE_REPORT_STATUS(id))(payload),
  getCaseFileSignedUrl: (id: number) => request(METHODS.GET, API.CASE.GET_CASE_FILE_SIGNED_URL(id))(),
  getCaseFileViewUrl: ({ caseId, fileId }: CaseFileDto) =>
    request(METHODS.GET, API.CASE.GET_CASE_FILE_VIEW_URL(caseId, fileId))(),
  createCaseFile: ({ caseId, kind, file, replace }: UploadCaseFileDto) =>
    request(METHODS.POST, API.CASE.CREATE_CASE_FILE(caseId))({ kind, file_name: file.name, size: file.size, replace }),
  // A plain axios call: the signed S3 url must not get the API's auth headers.
  uploadFileToStorage: (url: string, file: File, contentType: string) =>
    axios.put(url, file, { headers: { "Content-Type": contentType } }),
  confirmCaseFile: ({ caseId, fileId, replace }: CaseFileDto & Pick<UploadCaseFileDto, "replace">) =>
    request(
      METHODS.POST,
      API.CASE.CONFIRM_CASE_FILE(caseId, fileId),
    )({ replace }).then((response) => (response as { result: CheckedCaseFile }).result),
  deleteCaseFile: ({ caseId, fileId }: CaseFileDto) =>
    request(METHODS.DELETE, API.CASE.DELETE_CASE_FILE(caseId, fileId))(),
  discardTemporaryCaseFiles: ({ caseId, kind }: CaseDocumentDto) =>
    request(METHODS.DELETE, API.CASE.DISCARD_TEMPORARY_CASE_FILES(caseId, kind))(),
  parseCaseDocument: ({ caseId, kind, fileIds, replacedFileIds }: ParseCaseDocumentDto) =>
    request(
      METHODS.POST,
      API.CASE.PARSE_CASE_DOCUMENT(caseId, kind),
    )({
      file_ids: fileIds,
      replaced_file_ids: replacedFileIds,
    }),
  retryCaseDocument: ({ caseId, kind }: CaseDocumentDto) =>
    request(METHODS.POST, API.CASE.RETRY_CASE_DOCUMENT(caseId, kind))(),
  removeCaseDocument: ({ caseId, kind, clearText, clearEditedFields }: RemoveCaseDocumentDto) =>
    request(
      METHODS.POST,
      API.CASE.REMOVE_CASE_DOCUMENT(caseId, kind),
    )({
      clear_text: clearText,
      clear_edited_fields: clearEditedFields,
    }),
  getMedicalRecordsResult: (caseId: number) => request(METHODS.GET, API.CASE.GET_MEDICAL_RECORDS_RESULT(caseId))(),
  chooseMedicalFieldValue: ({ caseId, field, choice }: ChooseMedicalFieldValueDto) =>
    request(METHODS.POST, API.CASE.CHOOSE_MEDICAL_FIELD_VALUE(caseId))({ field, choice }),
  verifyMedicalField: ({ caseId, field }: VerifyMedicalFieldDto) =>
    request(METHODS.POST, API.CASE.VERIFY_MEDICAL_FIELD(caseId))({ field }),
  payForCase: (id: number) => request(METHODS.POST, API.CASE.PAY_FOR_CASE(id))(),
  updateCase: (id: number, payload: Omit<CaseModel, "id">) => request(METHODS.PATCH, API.CASE.UPDATE_CASE(id))(payload),
};
